
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RefreshCw } from "lucide-react";
import type { PrizeAllocation, University } from "./types";

interface UniversityResetControlsProps {
  universities: University[];
  allocations: PrizeAllocation[];
  isResetting: boolean;
  onReset: (universityId: number) => void;
}

export function UniversityResetControls({
  universities,
  allocations,
  isResetting,
  onReset
}: UniversityResetControlsProps) {
  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold mb-4">Reset Allocations by University</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {universities.map((university) => {
          const universityAllocations = allocations.filter(
            (a) => a.university_id === university.id
          );
          const totalAllocated = universityAllocations.reduce(
            (sum, a) => sum + a.allocated_count,
            0
          );

          return (
            <Card key={university.id} className="p-4">
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">{university.name}</h4>
                <p className="text-sm text-gray-600">
                  Total Allocated: {totalAllocated}
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      disabled={isResetting || totalAllocated === 0}
                    >
                      <RefreshCw className={`h-4 w-4 ${isResetting ? 'animate-spin' : ''}`} />
                      Reset Allocations
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset Allocations</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to reset all allocations for {university.name}? 
                        This will set all allocation counts back to 0.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => onReset(university.id)}
                        disabled={isResetting}
                      >
                        Reset
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
